/**
 * Profil-fane komponent
 */

import React, { useState } from 'react';
import { User, LogOut, Activity, TrendingUp, Calendar, Clock, Heart, Zap, Link, Trash2 } from 'lucide-react';
import { AVATARS } from '../constants/config';
import { updateUserProfile, deleteActivity, deleteAllUserData, updateLeaderboard } from '../services/firestore';
import { signOut, deleteUser } from '../services/auth';
import { initiateStravaAuth, deauthorizeStrava } from '../services/strava';
import { formatDuration, formatPace, formatKmH } from '../utils/formatters';
import { PrivacyConsentModal } from './PrivacyConsentModal';

export const ProfileTab = ({ user, userProfile, activities, leaderboard }) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState(userProfile?.name || '');
  const [selectedAvatar, setSelectedAvatar] = useState(userProfile?.avatar || '🏃‍♂️');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const userStats = leaderboard.find(l => l.id === user?.uid);
  const totalKm = userStats?.totalKm || 0;
  const totalActivities = activities?.length || 0;

  const handleSaveName = async () => {
    if (!name.trim()) return;
    
    try {
      // Oppdater profil
      await updateUserProfile(user.uid, {
        name: name.trim(),
        avatar: selectedAvatar
      });
      
      // Oppdater leaderboard med nytt navn
      await updateLeaderboard(user.uid, {
        name: name.trim(),
        avatar: selectedAvatar
      });
      
      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Kunne ikke oppdatere profil: ' + error.message);
    }
  };

  const handleAvatarSelect = async (avatar) => {
    setSelectedAvatar(avatar);
    try {
      const updatedName = userProfile?.name || user?.displayName || 'Løper';
      
      // Oppdater profil
      await updateUserProfile(user.uid, {
        name: updatedName,
        avatar: avatar
      });
      
      // Oppdater leaderboard med ny avatar
      await updateLeaderboard(user.uid, {
        name: updatedName,
        avatar: avatar
      });
      
      setShowAvatarPicker(false);
    } catch (error) {
      console.error('Error updating avatar:', error);
      alert('Kunne ikke oppdatere avatar: ' + error.message);
    }
  };

  const handleSignOut = async () => {
    if (confirm('Er du sikker på at du vil logge ut?')) {
      try {
        await signOut();
      } catch (error) {
        console.error('Error signing out:', error);
        alert('Kunne ikke logge ut: ' + error.message);
      }
    }
  };

  const handleDeleteAccount = async () => {
    try {
      console.log('handleDeleteAccount called');
      
      // Slett all brukerdata fra Firestore
      await deleteAllUserData(user.uid);
      
      console.log('deleteAllUserData completed');
      
      // Slett Firebase Auth-bruker
      await deleteUser();
      
      alert('Brukerkonto slettet');
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Kunne ikke slette brukerkonto: ' + error.message);
    }
  };

  const handleConnectStrava = () => {
    initiateStravaAuth();
  };

  const handleDisconnectStrava = async () => {
    if (confirm('Er du sikker på at du vil koble fra Strava?')) {
      try {
        // Prøv å deauthorize på Strava, men fortsett selv om det feiler
        try {
          if (userProfile?.stravaAccessToken) {
            await deauthorizeStrava(userProfile.stravaAccessToken);
          }
        } catch (deauthError) {
          console.warn('Could not deauthorize on Strava (token may be expired):', deauthError);
          // Fortsett likevel - vi fjerner tilkoblingen lokalt
        }
        
        // Fjern tilkoblingen fra Firestore
        await updateUserProfile(user.uid, {
          stravaConnected: false,
          stravaAccessToken: null,
          stravaRefreshToken: null,
          stravaExpiresAt: null,
          stravaAthleteId: null
        });
        
        alert('Strava er koblet fra');
      } catch (error) {
        console.error('Error disconnecting Strava:', error);
        alert('Kunne ikke koble fra Strava: ' + error.message);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <div className="bg-white p-6 rounded-[2.5rem] shadow-xl shadow-orange-100/40 border border-orange-50">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setShowAvatarPicker(true)}
            className="text-6xl hover:scale-110 transition-transform"
          >
            {selectedAvatar}
          </button>
          <div className="flex-1 min-w-0">
            {isEditingName ? (
              <div className="space-y-2">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-2 bg-slate-50 rounded-xl border-none outline-none font-bold"
                  placeholder="Ditt navn"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveName}
                    className="flex-1 py-2 bg-orange-600 text-white rounded-xl font-bold text-sm"
                  >
                    Lagre
                  </button>
                  <button
                    onClick={() => setIsEditingName(false)}
                    className="flex-1 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl font-black text-slate-800 tracking-tight">
                  {userProfile?.name || user?.displayName || 'Løper'}
                </h2>
                <button
                  onClick={() => {
                    setName(userProfile?.name || user?.displayName || '');
                    setIsEditingName(true);
                  }}
                  className="text-xs font-bold text-orange-600 hover:text-orange-700"
                >
                  Rediger navn
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-orange-50 p-4 rounded-2xl">
            <p className="text-[9px] font-black uppercase tracking-widest text-orange-400 mb-1">
              Totalt
            </p>
            <p className="text-3xl font-black text-slate-800 tabular-nums">
              {totalKm.toFixed(1)}
              <span className="text-sm text-slate-400 ml-1">km</span>
            </p>
          </div>
          <div className="bg-slate-50 p-4 rounded-2xl">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
              Økter
            </p>
            <p className="text-3xl font-black text-slate-800 tabular-nums">
              {totalActivities}
            </p>
          </div>
        </div>
      </div>

      {/* Avatar Picker Modal */}
      {showAvatarPicker && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-black text-lg text-slate-800 mb-4">
              Velg Avatar
            </h3>
            <div className="grid grid-cols-5 gap-3 mb-4">
              {AVATARS.map(avatar => (
                <button
                  key={avatar}
                  onClick={() => handleAvatarSelect(avatar)}
                  className={`text-4xl p-3 rounded-2xl hover:bg-orange-50 transition-colors ${
                    selectedAvatar === avatar ? 'bg-orange-100 ring-2 ring-orange-500' : 'bg-slate-50'
                  }`}
                >
                  {avatar}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowAvatarPicker(false)}
              className="w-full py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors"
            >
              Lukk
            </button>
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div className="space-y-3">
        <h3 className="font-black text-[10px] uppercase tracking-[0.15em] text-slate-400 px-1">
          Aktivitetslogg
        </h3>

        {activities && activities.length > 0 ? (
          <div className="space-y-2">
            {activities
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .slice(0, showAllActivities ? activities.length : 3)
              .map(activity => (
                <ActivityCard 
                  key={activity.id} 
                  activity={activity}
                  userId={user?.uid}
                  onDelete={async (activityId) => {
                    if (confirm('Er du sikker på at du vil slette denne aktiviteten?')) {
                      try {
                        await deleteActivity(user.uid, activityId);
                      } catch (error) {
                        console.error('Error deleting activity:', error);
                        alert('Kunne ikke slette aktivitet: ' + error.message);
                      }
                    }
                  }}
                />
              ))}
            
            {activities.length > 3 && (
              <button
                onClick={() => setShowAllActivities(!showAllActivities)}
                className="w-full py-3 bg-slate-50 text-slate-600 font-bold text-sm rounded-xl hover:bg-slate-100 transition-colors"
              >
                {showAllActivities ? 'Vis mindre' : `Vis alle (${activities.length})`}
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white p-8 rounded-2xl text-center">
            <Activity className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-sm font-bold">
              Ingen aktiviteter ennå
            </p>
            <p className="text-slate-300 text-xs mt-1">
              Synkroniser for å legge til aktiviteter
            </p>
          </div>
        )}
      </div>

      {/* Sign Out Button */}
      <button
        onClick={handleSignOut}
        className="w-full py-4 bg-red-50 text-red-600 font-black rounded-2xl hover:bg-red-100 transition-colors flex items-center justify-center gap-2 uppercase tracking-[0.15em] text-[11px]"
      >
        <LogOut className="w-4 h-4" />
        Logg ut
      </button>

      {/* Strava Connection */}
      {!userProfile?.stravaConnected ? (
        <button
          onClick={initiateStravaAuth}
          className="w-full py-4 bg-[#FC4C02] text-white font-bold rounded-2xl hover:bg-[#E34402] transition-colors flex items-center justify-center gap-3 text-sm"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
          </svg>
          Connect with Strava
        </button>
      ) : (
        <div className="bg-green-50 p-4 rounded-2xl border border-green-200">
          <p className="text-sm font-bold text-green-700 flex items-center justify-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
            </svg>
            Koblet til Strava
          </p>
          <p className="text-xs text-green-600 mt-2 text-center">
            Klikk på synkroniseringsknappen for å hente aktiviteter
          </p>
          <div className="mt-3 pt-3 border-t border-green-200">
            <div className="flex items-center justify-center gap-2 text-xs text-green-600 mb-3">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
              </svg>
              Powered by Strava
            </div>
            <button
              onClick={handleDisconnectStrava}
              className="w-full py-2 bg-white text-red-600 font-bold rounded-xl hover:bg-red-50 transition-colors text-xs border border-red-200"
            >
              Koble fra Strava
            </button>
          </div>
        </div>
      )}

      {/* Privacy Policy Link */}
      <div className="text-center mt-6">
        <button
          onClick={() => setShowPrivacyModal(true)}
          className="text-xs text-slate-500 hover:text-orange-600 transition-colors inline-flex items-center gap-1"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          Personvernerklæring
        </button>
      </div>

      {/* Delete Account Button */}
      <button
        onClick={() => setShowDeleteConfirm(true)}
        className="w-full py-2 text-slate-400 hover:text-slate-600 font-medium text-xs transition-colors"
      >
        Slett bruker
      </button>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-black text-lg text-slate-800 mb-2">
              Slett brukerkonto?
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Er du sikker på at du vil slette brukerkontoen din? Dette vil permanent slette:
            </p>
            <ul className="text-sm text-slate-600 mb-6 space-y-1 list-disc list-inside">
              <li>Din profil</li>
              <li>Alle dine aktiviteter</li>
              <li>Din leaderboard-posisjon</li>
              <li>Ditt medlemskap i konkurranser</li>
            </ul>
            <p className="text-sm font-bold text-red-600 mb-6">
              Dette kan ikke angres!
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteAccount}
                className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors"
              >
                Slett
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Privacy Modal */}
      {showPrivacyModal && (
        <PrivacyConsentModal
          onAccept={() => setShowPrivacyModal(false)}
          onDecline={() => setShowPrivacyModal(false)}
          isLoggedIn={true}
        />
      )}
    </div>
  );
};

// Activity Card Component
const ActivityCard = ({ activity, userId, onDelete }) => {
  const [showDetails, setShowDetails] = useState(false);

  const date = new Date(activity.date);
  const pace = formatPace(activity.duration, activity.km);
  const kmh = formatKmH(activity.duration, activity.km);

  // Debug: log activity data when details are shown
  React.useEffect(() => {
    if (showDetails) {
      console.log('Activity data:', {
        avg_heartrate: activity.avg_heartrate,
        max_heartrate: activity.max_heartrate,
        avg_speed: activity.avg_speed,
        max_speed: activity.max_speed
      });
    }
  }, [showDetails, activity]);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
            <Activity className="w-6 h-6 text-orange-600" />
          </div>
          <div className="text-left">
            <p className="font-black text-sm text-slate-800">
              {activity.km.toFixed(2)} km
            </p>
            <p className="text-[9px] font-bold text-slate-400">
              {date.toLocaleDateString('nb-NO', { 
                day: 'numeric', 
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-slate-600">
            {formatDuration(activity.duration)}
          </p>
          <p className="text-[9px] font-bold text-orange-600">
            {pace}
          </p>
        </div>
      </button>

      {showDetails && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-100 bg-slate-50">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-[8px] font-bold text-slate-400 uppercase">Tempo</p>
                <p className="text-sm font-black text-slate-700">{pace}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-[8px] font-bold text-slate-400 uppercase">Hastighet</p>
                <p className="text-sm font-black text-slate-700">{kmh}</p>
              </div>
            </div>
          </div>

          {/* Puls Section */}
          {(activity.avg_heartrate || activity.max_heartrate) && (
            <div className="mb-3 p-3 bg-red-50 rounded-xl">
              <p className="text-[9px] font-black text-red-600 uppercase tracking-wider mb-2">
                Puls
              </p>
              <div className="grid grid-cols-2 gap-2">
                {activity.avg_heartrate && (
                  <div>
                    <p className="text-[8px] font-bold text-red-400 uppercase">Snitt</p>
                    <p className="text-lg font-black text-red-700">{Math.round(activity.avg_heartrate)} bpm</p>
                  </div>
                )}
                {activity.max_heartrate && (
                  <div>
                    <p className="text-[8px] font-bold text-red-400 uppercase">Maks</p>
                    <p className="text-lg font-black text-red-700">{Math.round(activity.max_heartrate)} bpm</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Hastighet Section */}
          {(activity.avg_speed || activity.max_speed) && (
            <div className="mb-3 p-3 bg-orange-50 rounded-xl">
              <p className="text-[9px] font-black text-orange-600 uppercase tracking-wider mb-2">
                Hastighet
              </p>
              <div className="grid grid-cols-3 gap-2">
                {activity.avg_speed && (
                  <div>
                    <p className="text-[8px] font-bold text-orange-400 uppercase">Snitt</p>
                    <p className="text-sm font-black text-orange-700">
                      {(activity.avg_speed * 3.6).toFixed(1)} km/t
                    </p>
                  </div>
                )}
                {activity.max_speed && (
                  <div>
                    <p className="text-[8px] font-bold text-orange-400 uppercase">Maks</p>
                    <p className="text-sm font-black text-orange-700">
                      {(activity.max_speed * 3.6).toFixed(1)} km/t
                    </p>
                  </div>
                )}
                {activity.fastest_km_seconds && (
                  <div>
                    <p className="text-[8px] font-bold text-orange-400 uppercase">Raskeste km</p>
                    <p className="text-sm font-black text-orange-700">
                      {formatPace(activity.fastest_km_seconds, 1)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(activity.id);
            }}
            className="w-full py-2 bg-red-50 text-red-600 rounded-xl font-bold text-xs hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Slett aktivitet
          </button>
        </div>
      )}
    </div>
  );
};
